// @ts-nocheck
import { yieldToMainThread } from '@theme/utilities';

const SIZE_ORDER = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL'];
const MAX_COLOR_SWATCHES = 4;
const ADD_TO_CART_SUCCESS_DURATION_MS = 3500;

const CURRENCY_DECIMALS = {
  BHD: 3, BIF: 0, BYR: 0, CLF: 4, CLP: 0, DJF: 0, GNF: 0, IQD: 3, ISK: 0,
  JOD: 3, JPY: 0, KMF: 0, KRW: 0, KWD: 3, LYD: 3, MRO: 5, OMR: 3, PYG: 0,
  RWF: 0, TND: 3, UGX: 0, UYI: 0, UYW: 4, VND: 0, VUV: 0, XAF: 0, XAG: 0,
  XAU: 0, XBA: 0, XBB: 0, XBC: 0, XBD: 0, XDR: 0, XOF: 0, XPD: 0, XPF: 0,
  XPT: 0, XSU: 0, XTS: 0, XUA: 0,
};

/** @type {Map<string, object>} */
const productDataCache = new Map();

/** @type {Map<string, string>} */
const swatchColorCache = new Map();

/** Product IDs that Storefront GraphQL returned as null (draft/archived/unpublished). */
const unavailableColorVariantIds = new Set();

/** Product IDs that have been requested via Storefront GraphQL. */
const queriedColorVariantIds = new Set();

const SEARCH_MODAL_PRODUCT_STORE_KEY = 'searchModalProductStore';

/**
 * Returns the window-backed product store keyed by product id.
 * @returns {Record<string, object>}
 */
export function getSearchModalProductStore() {
  const w = /** @type {Window & { theme?: Record<string, unknown> }} */ (window);
  w.theme = w.theme || {};

  if (!w.theme[SEARCH_MODAL_PRODUCT_STORE_KEY]) {
    w.theme[SEARCH_MODAL_PRODUCT_STORE_KEY] = Object.create(null);
  }

  return /** @type {Record<string, object>} */ (w.theme[SEARCH_MODAL_PRODUCT_STORE_KEY]);
}

/**
 * @param {object} product
 * @returns {boolean}
 */
function isGraphqlEnrichedProduct(product) {
  return product?.enrichedBy === 'storefront';
}

/**
 * Merges GraphQL swatch/color-variant metafields into an existing Boost/API product
 * without replacing prices, images, or variant inventory from the API payload.
 * @param {object} apiProduct
 * @param {object} graphqlProduct
 * @returns {object}
 */
function mergeGraphqlSwatchIntoApiProduct(apiProduct, graphqlProduct) {
  const existingMetafields = Array.isArray(apiProduct.metafields) ? apiProduct.metafields.slice() : [];
  const graphqlMetafields = Array.isArray(graphqlProduct.metafields) ? graphqlProduct.metafields : [];

  graphqlMetafields.forEach((field) => {
    if (!field?.key || field.value == null || field.value === '') return;
    const index = existingMetafields.findIndex((item) => item?.key === field.key);
    if (index >= 0) {
      existingMetafields[index] = Object.assign({}, existingMetafields[index], field);
    } else {
      existingMetafields.push(field);
    }
  });

  return Object.assign({}, apiProduct, {
    metafields: existingMetafields,
    swatchEnrichedBy: 'storefront',
  });
}

/**
 * Builds a window-store entry with swatch and color-variant metadata.
 * @param {object} product
 * @returns {object}
 */
function buildSearchModalStoreEntry(product) {
  const id = String(product.id);
  const swatchColor = getSwatchColorFromProduct(product) || swatchColorCache.get(id) || '';

  return {
    ...product,
    swatchColor,
    colorVariantIds: getColorVariantProductIds(product),
  };
}

/**
 * @param {object|undefined} existing
 * @param {object} incoming
 * @returns {boolean}
 */
function shouldReplaceSearchModalStoreEntry(existing, incoming) {
  if (!existing) return true;

  const existingEnriched = isGraphqlEnrichedProduct(existing);
  const incomingEnriched = isGraphqlEnrichedProduct(incoming);

  if (!existingEnriched && incomingEnriched) return true;
  if (existingEnriched && !incomingEnriched) return false;

  return true;
}

/**
 * Stores one or more products in window.theme.searchModalProductStore by id.
 * GraphQL-enriched entries are not overwritten by lighter Boost payloads.
 * @param {object|object[]|null|undefined} products
 */
export function storeSearchModalProducts(products) {
  if (!products) return;

  const store = getSearchModalProductStore();
  const list = Array.isArray(products) ? products : [products];

  list.forEach((product) => {
    if (!product?.id) return;

    const id = String(product.id);
    const incoming = buildSearchModalStoreEntry(product);
    const existing = store[id];

    if (!shouldReplaceSearchModalStoreEntry(existing, incoming)) return;

    store[id] = incoming;
  });
}

/**
 * Re-syncs window-store entries from the in-memory product cache.
 * Also walks colorVariantIds so related products are stored under their own ids.
 * @param {string[]} productIds
 */
function refreshSearchModalProductStore(productIds) {
  const idsToSync = new Set(productIds.map((id) => String(id)).filter(Boolean));
  const queue = [...idsToSync];

  while (queue.length) {
    const id = queue.shift();
    const product = getCachedProduct(id);
    if (!product?.id) continue;

    storeSearchModalProducts(product);

    getColorVariantProductIds(product).forEach((variantId) => {
      const normalized = String(variantId);
      if (!idsToSync.has(normalized)) {
        idsToSync.add(normalized);
        queue.push(normalized);
      }
    });
  }
}

/**
 * @param {string} productId
 * @returns {object|undefined}
 */
export function getSearchModalProduct(productId) {
  return getSearchModalProductStore()[String(productId)];
}

/** Hardcoded Storefront token for GraphQL (search modal, cart drawer, search page). */
const HARDCODED_STOREFRONT_ACCESS_TOKEN = 'c068f4855b00e4675bc5153b9564158d';

/** @type {object} */
let config = {
  shop: 'basic-collecton.myshopify.com',
  storefrontAccessToken: HARDCODED_STOREFRONT_ACCESS_TOKEN,
  storefrontApiVersion: '2025-01',
  moneyFormat: '{{amount}}',
  currency: 'USD',
  country: '',
  icons: {},
};

const COLOR_VARIANTS_PRODUCT_SELECTION = `
  ... on Product {
    id
    handle
    title
    featuredImage {
      url(transform: { maxWidth: 900 })
    }
    images(first: 10) {
      nodes {
        url(transform: { maxWidth: 900 })
      }
    }
    priceRange {
      minVariantPrice {
        amount
        currencyCode
      }
    }
    compareAtPriceRange {
      minVariantPrice {
        amount
        currencyCode
      }
      maxVariantPrice {
        amount
        currencyCode
      }
    }
    variants(first: 25) {
      nodes {
        id
        title
        availableForSale
        price {
          amount
          currencyCode
        }
        compareAtPrice {
          amount
          currencyCode
        }
      }
    }
    swatchColor: metafield(namespace: "custom", key: "swatch_color") {
      value
    }
    productCardTag: metafield(namespace: "custom", key: "product_card_tag") {
      value
    }
    colorVariants: metafield(namespace: "custom", key: "variants") {
      value
    }
  }
`;

/**
 * Builds the color-variant Storefront query, optionally scoped to a market country
 * so MoneyV2 amount + currencyCode match the active currency.
 * @param {boolean} withCountry
 * @returns {string}
 */
function buildColorVariantsQuery(withCountry) {
  if (withCountry) {
    return `
      query SearchDrawerColorVariants($ids: [ID!]!, $country: CountryCode!) @inContext(country: $country) {
        nodes(ids: $ids) {
          ${COLOR_VARIANTS_PRODUCT_SELECTION}
        }
      }
    `;
  }

  return `
    query SearchDrawerColorVariants($ids: [ID!]!) {
      nodes(ids: $ids) {
        ${COLOR_VARIANTS_PRODUCT_SELECTION}
      }
    }
  `;
}

/**
 * Applies storefront and formatting configuration for search drawer product cards.
 * Storefront GraphQL always uses the hardcoded token for now.
 * @param {object} nextConfig
 */
export function configureSearchDrawerProductCard(nextConfig) {
  config = { ...config, ...nextConfig };
  config.storefrontAccessToken = HARDCODED_STOREFRONT_ACCESS_TOKEN;
}

/**
 * @returns {Map<string, object>}
 */
export function getProductDataCache() {
  return productDataCache;
}

/**
 * @returns {Map<string, string>}
 */
function getSwatchColorCache() {
  return swatchColorCache;
}

/**
 * @returns {object}
 */
function getSearchDrawerProductConfig() {
  return config;
}

/**
 * @param {string} productId
 * @returns {object|undefined}
 */
export function getCachedProduct(productId) {
  return productDataCache.get(String(productId));
}

/**
 * Reads custom.swatch_color from product metafields.
 * @param {object} product
 * @returns {string}
 */
function getSwatchColorFromProduct(product) {
  const metafields = Array.isArray(product?.metafields) ? product.metafields : [];
  const swatchColorValue = metafields.find((field) => field?.key === 'swatch_color')?.value;

  return parseSwatchColor(swatchColorValue);
}

/**
 * @param {string} productId
 * @param {object} product
 */
export function setCachedProduct(productId, product) {
  productDataCache.set(String(productId), product);

  const swatchColor = getSwatchColorFromProduct(product);

  if (swatchColor) {
    swatchColorCache.set(String(productId), swatchColor);
  }

  storeSearchModalProducts(product);
  refreshSearchModalProductStore(getColorVariantProductIds(product));
}

/**
 * Renders ready, loading, and success layers for add-to-cart buttons.
 * @param {string} label
 * @returns {string}
 */
function buildAddToCartButtonStatesMarkup(label) {
  return `<span class="btn-state-ready">${escapeHtml(label)}</span>
          <span class="btn-state-loading" aria-hidden="true">
            <svg height="18" width="18" class="svg-loader" viewBox="0 0 18 18" aria-hidden="true">
              <circle class="svg-loader__track" r="7" cx="9" cy="9"></circle>
              <g transform="rotate(-90 9 9)">
                <circle class="svg-loader__progress" r="7" cx="9" cy="9"></circle>
              </g>
            </svg>
          </span>
          <span class="btn-state-complete" style="--primary: var(--color-foreground);">&nbsp;</span>`;
}

/**
 * Escapes HTML special characters.
 * @param {string} value
 * @returns {string}
 */
function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Extracts the numeric ID from a Shopify GID or numeric string.
 * @param {string} gid
 * @returns {string}
 */
function extractNumericId(gid) {
  const match = String(gid || '').match(/(\d+)$/);
  return match ? match[1] : String(gid || '');
}

/**
 * Builds a Storefront API product GID from a numeric product ID.
 * @param {string|number} productId
 * @returns {string}
 */
function toProductGid(productId) {
  return `gid://shopify/Product/${extractNumericId(productId)}`;
}

/**
 * Parses a custom.swatch_color metafield value into a CSS color.
 * @param {string} value
 * @returns {string}
 */
function parseSwatchColor(value) {
  if (!value) return '';

  const trimmed = String(value).trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('#')) return trimmed;
  if (/^[0-9a-fA-F]{6}$/.test(trimmed)) return `#${trimmed}`;

  try {
    const parsed = JSON.parse(trimmed);
    if (typeof parsed === 'string') return parseSwatchColor(parsed);
    if (parsed?.hex) return parseSwatchColor(parsed.hex);
  } catch {
    return trimmed;
  }

  return trimmed;
}

/**
 * Returns true when a parsed swatch color should be treated as white.
 * @param {string} color
 * @returns {boolean}
 */
function isWhiteSwatchColor(color) {
  if (!color) return false;

  const normalized = String(color).trim().toLowerCase();

  if (normalized === 'white') return true;
  if (normalized === '#fff' || normalized === '#ffffff') return true;

  return /^rgba?\(\s*255\s*,\s*255\s*,\s*255(?:\s*,\s*(?:1|1\.0+|0?\.\d+))?\s*\)$/.test(normalized);
}

/**
 * Logs swatch background colors grouped by product.
 * @param {string} productId
 * @param {string[]} colorVariantIds
 */
function logProductSwatchColors(productId, colorVariantIds) {
  if (!productId) return;

  const cache = getSwatchColorCache();
  const swatchColors = (Array.isArray(colorVariantIds) ? colorVariantIds : [])
    .map((variantId) => cache.get(String(variantId)) || null)
    .filter(Boolean);
}

/**
 * Active storefront currency code (e.g. CAD).
 * @returns {string}
 */
function getActiveCurrencyCode() {
  const shopify = typeof window !== 'undefined' ? window.Shopify : null;
  const boostCfg = typeof window !== 'undefined' ? window.BoostFilterConfig || {} : {};

  return String(
    (shopify && shopify.currency && shopify.currency.active) ||
      boostCfg.currency ||
      config.currency ||
      ''
  ).trim();
}

/**
 * Active market country code for Storefront @inContext (e.g. CA).
 * @returns {string}
 */
function getActiveCountryCode() {
  const shopify = typeof window !== 'undefined' ? window.Shopify : null;
  const boostCfg = typeof window !== 'undefined' ? window.BoostFilterConfig || {} : {};
  const theme = typeof window !== 'undefined' ? window.theme || {} : {};

  return String(
    config.country ||
      boostCfg.country ||
      theme.countryCode ||
      (shopify && shopify.country) ||
      ''
  )
    .trim()
    .toUpperCase();
}

/**
 * Reads a Storefront MoneyV2 value (amount + currencyCode).
 * @param {object|null|undefined} money
 * @returns {{ amount: number, currencyCode: string }}
 */
function readMoneyV2(money) {
  return {
    amount: Number(money?.amount || 0),
    currencyCode: String(money?.currencyCode || getActiveCurrencyCode() || '').trim().toUpperCase(),
  };
}

/**
 * Writes base + currency-suffixed price fields from a MoneyV2 value.
 * @param {object} target
 * @param {string} baseKey
 * @param {{ amount: number, currencyCode: string }} money
 * @param {boolean} [allowNull=false]
 */
function assignLocalizedPrice(target, baseKey, money, allowNull = false) {
  const amount = money.amount;
  const hasAmount = Number.isFinite(amount) && amount > 0;

  if (allowNull && !hasAmount) {
    target[baseKey] = null;
    return;
  }

  target[baseKey] = Number.isFinite(amount) ? amount : 0;

  if (money.currencyCode) {
    target[`${baseKey}_${money.currencyCode.toLowerCase()}`] = target[baseKey];
  }
}

/**
 * Prefers Boost currency-specific price fields (e.g. price_min_cad) when present.
 * @param {object} product
 * @param {string} baseKey
 * @returns {number}
 */
export function resolveBoostPrice(product, baseKey) {
  const currency = getActiveCurrencyCode().toLowerCase();

  if (currency) {
    const localized = product?.[`${baseKey}_${currency}`];
    if (localized != null && localized !== '') return Number(localized);
  }

  if (baseKey === 'price_min') {
    return Number(product?.price_min ?? product?.price ?? 0);
  }

  if (baseKey === 'price_max') {
    return Number(product?.price_max ?? product?.price ?? 0);
  }

  return product?.[baseKey] != null ? Number(product[baseKey]) : NaN;
}

/**
 * Formats a major-unit price (e.g. Boost API) using Shopify money format.
 * @param {number|string} amount
 * @param {string} [moneyFormat]
 * @param {string} [currency]
 * @returns {string}
 */
function formatMajorMoney(amount, moneyFormat, currency) {
  const format = String(moneyFormat || config.moneyFormat);
  const currencyCode = String(currency || getActiveCurrencyCode() || config.currency || 'USD').toUpperCase();
  const currencyPrecision = CURRENCY_DECIMALS[currencyCode] ?? 2;
  const safeValue = Number.isFinite(Number(amount)) ? Number(amount) : 0;

  return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, placeholder) => {
    if (placeholder === 'currency') return currencyCode;

    let thousandsSeparator = ',';
    let decimalSeparator = '.';
    let precision = currencyPrecision;

    switch (placeholder) {
      case 'amount':
        break;
      case 'amount_no_decimals':
        precision = 0;
        break;
      case 'amount_with_comma_separator':
        thousandsSeparator = '.';
        decimalSeparator = ',';
        break;
      case 'amount_no_decimals_with_comma_separator':
        thousandsSeparator = '.';
        precision = 0;
        break;
      case 'amount_no_decimals_with_space_separator':
        thousandsSeparator = ' ';
        precision = 0;
        break;
      case 'amount_with_space_separator':
        thousandsSeparator = ' ';
        decimalSeparator = ',';
        break;
      case 'amount_with_period_and_space_separator':
        thousandsSeparator = ' ';
        decimalSeparator = '.';
        break;
      case 'amount_with_apostrophe_separator':
        thousandsSeparator = "'";
        decimalSeparator = '.';
        break;
      default:
        return '';
    }

    const roundedNumber = safeValue.toFixed(precision);
    let [whole, fraction] = roundedNumber.split('.');
    if (!whole) whole = '0';
    if (!fraction) fraction = '';

    whole = whole.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousandsSeparator);

    return precision <= 0 ? whole : `${whole}${decimalSeparator}${fraction.padEnd(precision, '0')}`;
  });
}

/**
 * Builds display data for the neo quick view modal from the search modal store.
 * @param {string|number} productId
 * @returns {object|null}
 */
export function buildQuickViewProductData(productId) {
  const id = String(productId || '');
  const product = getSearchModalProduct(id) || getCachedProduct(id);
  if (!product?.handle) return null;

  const config = getSearchDrawerProductConfig();
  const store = getSearchModalProductStore();
  const colorVariantIds = getCanonicalColorVariantProductIds(product);

  const colorVariants = colorVariantIds.map((variantId) => {
    const variantProduct = store[variantId] || getCachedProduct(variantId);
    const swatchColor =
      variantProduct?.swatchColor ||
      getSwatchColorFromProduct(variantProduct) ||
      swatchColorCache.get(variantId) ||
      '';

    return {
      id: variantId,
      swatchColor,
      isWhite: isWhiteSwatchColor(swatchColor),
    };
  });

  const images = getProductImages(product);
  const priceMin = resolveBoostPrice(product, 'price_min');
  const priceMax = resolveBoostPrice(product, 'price_max');
  const price = Number.isFinite(priceMin) ? priceMin : priceMax;
  const compareAtMin = resolveBoostPrice(product, 'compare_at_price_min');
  const compareAtMax = resolveBoostPrice(product, 'compare_at_price_max');
  const compareAt = Number.isFinite(compareAtMin)
    ? compareAtMin
    : Number.isFinite(compareAtMax)
      ? compareAtMax
      : null;
  const isOnSale = Number(compareAt) > Number(price);
  const activeCurrency = getActiveCurrencyCode() || config.currency;

  return {
    productId: id,
    title: String(product.title || ''),
    handle: String(product.handle),
    productUrl: `/products/${encodeURIComponent(product.handle)}`,
    images,
    imageAlt: String(product.title || ''),
    price: formatMajorMoney(price, config.moneyFormat, activeCurrency),
    compareAt: isOnSale ? formatMajorMoney(compareAt, config.moneyFormat, activeCurrency) : '',
    colorVariants,
    sizeVariants: getSizeVariants(product.variants),
  };
}

/**
 * Executes a Storefront GraphQL request.
 * @param {string} query
 * @param {Record<string, unknown>} [variables]
 * @returns {Promise<object|null>}
 */
async function storefrontGraphql(query, variables) {
  const accessToken = HARDCODED_STOREFRONT_ACCESS_TOKEN;
  if (!accessToken) return null;

  const endpoint = `https://${config.shop}/api/${config.storefrontApiVersion}/graphql.json`;

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': accessToken,
      },
      body: JSON.stringify({ query, variables }),
    });

    const result = await response.json();
    if (!response.ok || result?.errors?.length) return null;

    return result?.data || null;
  } catch {
    return null;
  }
}

/**
 * Normalizes a Storefront API product node into the Boost card shape.
 * @param {object} node
 * @returns {object|null}
 */
function normalizeGraphqlProduct(node) {
  if (!node?.id || !node?.handle) return null;

  const productId = extractNumericId(node.id);
  const imageNodes = Array.isArray(node.images?.nodes) ? node.images.nodes : [];
  const imageUrls = imageNodes
    .map((image) => image?.url)
    .filter(Boolean)
    .map((url) => optimizeProductImageUrl(String(url), 400));

  if (node.featuredImage?.url) {
    const featured = optimizeProductImageUrl(String(node.featuredImage.url), 400);
    if (featured && !imageUrls.includes(featured)) {
      imageUrls.unshift(featured);
    }
  }

  const variants = Array.isArray(node.variants?.nodes)
    ? node.variants.nodes.map((variant) => {
        const price = readMoneyV2(variant.price);
        const compareAt = readMoneyV2(variant.compareAtPrice);
        const entry = {
          id: extractNumericId(variant.id),
          title: variant.title,
          available: variant.availableForSale !== false,
          price: price.amount,
          compare_at_price: compareAt.amount > 0 ? compareAt.amount : null,
        };

        if (price.currencyCode) {
          entry[`price_${price.currencyCode.toLowerCase()}`] = price.amount;
        }
        if (compareAt.currencyCode && compareAt.amount > 0) {
          entry[`compare_at_price_${compareAt.currencyCode.toLowerCase()}`] = compareAt.amount;
        }

        return entry;
      })
    : [];

  const priceMin = readMoneyV2(node.priceRange?.minVariantPrice);
  const compareAtMin = readMoneyV2(node.compareAtPriceRange?.minVariantPrice);
  const compareAtMax = readMoneyV2(node.compareAtPriceRange?.maxVariantPrice);

  const product = {
    id: productId,
    handle: node.handle,
    title: node.title,
    enrichedBy: 'storefront',
    currency: priceMin.currencyCode || getActiveCurrencyCode(),
    images: Object.fromEntries(imageUrls.map((url, index) => [String(index + 1), url])),
    variants,
    metafields: [
      { key: 'swatch_color', value: node.swatchColor?.value },
      { key: 'product_card_tag', value: node.productCardTag?.value },
      { key: 'variants', value: node.colorVariants?.value },
    ].filter((field) => field.value),
  };

  assignLocalizedPrice(product, 'price_min', priceMin);
  assignLocalizedPrice(product, 'compare_at_price_min', compareAtMin, true);
  assignLocalizedPrice(product, 'compare_at_price_max', compareAtMax, true);

  return product;
}

/** Product IDs queued for the next coalesced GraphQL swatch fetch. */
const queuedColorVariantIds = new Set();

/** @type {Promise<void>|null} */
let coalescedColorVariantFetch = null;

/**
 * Fetches and caches product data for color variant IDs.
 * Draft/archived/unpublished products return null from Storefront API and are marked unavailable.
 * Concurrent callers are coalesced into one GraphQL request (ponytail: avoids N card stampede).
 * @param {string[]} productIds
 * @returns {Promise<void>}
 */
export async function fetchAndCacheColorVariantProducts(productIds) {
  (Array.isArray(productIds) ? productIds : []).forEach((id) => {
    const numericId = extractNumericId(id);
    if (numericId) queuedColorVariantIds.add(numericId);
  });

  if (coalescedColorVariantFetch) return coalescedColorVariantFetch;

  coalescedColorVariantFetch = Promise.resolve().then(async () => {
    const uniqueIds = [...queuedColorVariantIds];
    queuedColorVariantIds.clear();
    coalescedColorVariantFetch = null;
    await fetchAndCacheColorVariantProductsNow(uniqueIds);
  });

  return coalescedColorVariantFetch;
}

/**
 * @param {string[]} uniqueIds
 * @returns {Promise<void>}
 */
async function fetchAndCacheColorVariantProductsNow(uniqueIds) {
  const missingIds = uniqueIds.filter((id) => {
    if (unavailableColorVariantIds.has(String(id))) return false;

    const cached = getCachedProduct(id);
    if (!cached) return true;

    if (isGraphqlEnrichedProduct(cached)) return false;

    const hasImages = getProductImages(cached).length > 0;
    const hasSwatch = Boolean(getSwatchColorFromProduct(cached));

    if (cached.swatchEnrichedBy === 'storefront' && hasImages && hasSwatch) return false;

    if (!hasImages || !hasSwatch) return true;

    return false;
  });

  if (!missingIds.length || !HARDCODED_STOREFRONT_ACCESS_TOKEN) {
    refreshSearchModalProductStore(uniqueIds);
    return;
  }

  const country = getActiveCountryCode();
  const variables = {
    ids: missingIds.map(toProductGid),
  };

  if (country) variables.country = country;

  const data = await storefrontGraphql(buildColorVariantsQuery(Boolean(country)), variables);

  // Network/API failure — do not treat products as draft/archived.
  if (!data || !Array.isArray(data.nodes)) {
    refreshSearchModalProductStore(uniqueIds);
    return;
  }

  missingIds.forEach((id) => queriedColorVariantIds.add(String(id)));

  const nodes = data.nodes;

  missingIds.forEach((requestedId, index) => {
    const product = normalizeGraphqlProduct(nodes[index]);

    if (!product) {
      // Storefront omits draft/archived/unpublished products as null nodes.
      unavailableColorVariantIds.add(String(requestedId));
      return;
    }

    unavailableColorVariantIds.delete(String(product.id));

    // Keep Boost/API products as source of truth — only merge GraphQL swatch metafields.
    const existing = getCachedProduct(String(product.id));
    if (existing?.handle && !isGraphqlEnrichedProduct(existing)) {
      setCachedProduct(String(product.id), mergeGraphqlSwatchIntoApiProduct(existing, product));
      return;
    }

    setCachedProduct(String(product.id), product);
  });

  refreshSearchModalProductStore(uniqueIds);
}

/**
 * Returns true when a color-variant product ID should get a swatch button.
 * Draft/archived/unpublished IDs are never rendered once GraphQL has resolved them.
 * @param {string|number} productId
 * @returns {boolean}
 */
function isRenderableColorSwatch(productId) {
  const id = String(productId || '');
  if (!id) return false;
  if (unavailableColorVariantIds.has(id)) return false;

  const product = getCachedProduct(id);
  // Any cached product with a handle is showable (Boost/API or GraphQL).
  if (product?.handle) return true;

  // Queried but missing from cache = draft/archived/unpublished.
  if (queriedColorVariantIds.has(id)) return false;

  // Not queried yet — keep in list so hydrate can fetch; skeletons show until then.
  return true;
}

/**
 * Filters color-variant IDs to products that should display as swatches.
 * @param {string[]|null|undefined} colorVariantIds
 * @returns {string[]}
 */
function filterRenderableColorVariantIds(colorVariantIds) {
  return (Array.isArray(colorVariantIds) ? colorVariantIds : [])
    .map(String)
    .filter(isRenderableColorSwatch);
}

/**
 * @param {object} product
 * @returns {string}
 */
function getProductCardTag(product) {
  const metafields = Array.isArray(product?.metafields) ? product.metafields : [];
  const tagField = metafields.find((field) => field?.key === 'product_card_tag');

  if (tagField?.value) return String(tagField.value).trim();

  const tags = Array.isArray(product?.tags) ? product.tags : [];
  const normalizedTags = tags.map((tag) => String(tag).toLowerCase());

  if (normalizedTags.includes('final sale')) return 'FINAL SALE';
  if (normalizedTags.includes('back in stock')) return 'BACK IN STOCK';

  return '';
}

/**
 * Normalizes CDN image URLs so cards load reliably and quickly.
 * Boost often returns protocol-relative full-size assets; force https + width.
 * @param {string} src
 * @param {number} [width=800]
 * @returns {string}
 */
export function optimizeProductImageUrl(src, width = 800) {
  if (!src) return '';
  let url = String(src).trim();
  if (!url) return '';
  if (url.indexOf('//') === 0) url = `https:${url}`;

  try {
    const parsed = new URL(url, typeof window !== 'undefined' ? window.location.origin : 'https://cdn.shopify.com');
    parsed.searchParams.set('width', String(width));
    if (!parsed.searchParams.has('format')) parsed.searchParams.set('format', 'webp');
    return parsed.toString();
  } catch {
    const sep = url.indexOf('?') >= 0 ? '&' : '?';
    return `${url}${sep}width=${width}&format=webp`;
  }
}

/**
 * @param {object} product
 * @returns {string[]}
 */
function getProductImages(product) {
  if (Array.isArray(product?.images_info) && product.images_info.length) {
    return product.images_info
      .map((image) => image?.src || image?.url)
      .filter(Boolean)
      .map((src) => optimizeProductImageUrl(String(src), 400));
  }

  if (product?.images && typeof product.images === 'object') {
    return Object.keys(product.images)
      .sort((a, b) => Number(a) - Number(b))
      .map((key) => product.images[key])
      .filter(Boolean)
      .map((src) => optimizeProductImageUrl(String(src), 400));
  }

  return [];
}

/**
 * @param {object[]} variants
 * @returns {object[]}
 */
function getSizeVariants(variants) {
  if (!Array.isArray(variants)) return [];

  return [...variants].sort((a, b) => {
    const titleA = String(a?.title || '').toUpperCase();
    const titleB = String(b?.title || '').toUpperCase();
    const indexA = SIZE_ORDER.indexOf(titleA);
    const indexB = SIZE_ORDER.indexOf(titleB);

    if (indexA === -1 && indexB === -1) return titleA.localeCompare(titleB);
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;

    return indexA - indexB;
  });
}

/**
 * Parses color variant product IDs from the custom.variants metafield.
 * @param {object} product
 * @returns {string[]}
 */
function parseColorVariantIdsFromMetafield(product) {
  const metafields = Array.isArray(product?.metafields) ? product.metafields : [];
  const variantsField = metafields.find((field) => field?.key === 'variants');

  if (!variantsField?.value) return [];

  try {
    const parsed = JSON.parse(variantsField.value);
    if (!Array.isArray(parsed)) return [];

    return parsed
      .map((gid) => {
        const match = String(gid).match(/Product\/(\d+)/);
        return match ? match[1] : String(gid);
      })
      .filter(Boolean);
  } catch {
    return [];
  }
}

/**
 * Returns color variant product IDs with the current product always first.
 * @param {object} product
 * @returns {string[]}
 */
function getCanonicalColorVariantProductIds(product) {
  return getColorVariantProductIds(product);
}

/**
 * Parses color variant product IDs from the custom.variants metafield.
 * The current product is always first in the returned list.
 * @param {object} product
 * @returns {string[]}
 */
function getColorVariantProductIds(product) {
  const currentProductId = String(product?.id || '');
  const variantIds = parseColorVariantIdsFromMetafield(product);

  if (!currentProductId) return filterRenderableColorVariantIds(variantIds);

  const otherVariantIds = variantIds.filter((id) => String(id) !== currentProductId);
  return filterRenderableColorVariantIds([currentProductId, ...otherVariantIds]);
}

/**
 * Ensures the card's own product ID is the first swatch option.
 * Only for initial order when no stored swatch list exists yet.
 * @param {string[]} colorVariantIds
 * @param {object|null|undefined} product
 * @returns {string[]}
 */
function ensureCurrentProductFirstSwatch(colorVariantIds, product) {
  const currentProductId = String(product?.id || '');
  const ids = (Array.isArray(colorVariantIds) ? colorVariantIds : []).map(String).filter(Boolean);

  if (!currentProductId) return ids;
  if (!ids.length) return [currentProductId];

  const others = ids.filter((id) => id !== currentProductId);
  return [currentProductId, ...others];
}

/**
 * Resolves swatch IDs for render: keep stored order stable across color changes.
 * @param {string[]} storedIds
 * @param {object|null|undefined} product
 * @returns {string[]}
 */
function resolveColorVariantIdsForRender(storedIds, product) {
  const stored = filterRenderableColorVariantIds(storedIds);

  if (stored.length) return stored;

  return ensureCurrentProductFirstSwatch(
    filterRenderableColorVariantIds(getColorVariantProductIds(product)),
    product
  );
}

/**
 * Returns the active color variant index for the current product card.
 * @param {string[]} colorVariantIds
 * @param {object} product
 * @returns {number}
 */
function getActiveColorVariantIndex(colorVariantIds, product) {
  const currentProductId = String(product?.id || '');
  const matchedIndex = colorVariantIds.findIndex((id) => id === currentProductId);

  return matchedIndex >= 0 ? matchedIndex : 0;
}

/**
 * A reusable search drawer product card with image carousel, color swatches, and quick add.
 */
class SearchDrawerProductCard extends HTMLElement {
  /** @type {object|null} */
  #product = null;
  /** @type {import('swiper').default|null} */
  #swiper = null;
  /** @type {Promise<void>|null} */
  #swatchHydratePromise = null;
  /** @type {boolean} */
  #swiperScheduled = false;
  /** @type {boolean} */
  #swiperObserved = false;

  /** @type {Set<SearchDrawerProductCard>} */
  static #pendingSwiperInits = new Set();
  /** @type {boolean} */
  static #swiperDrainRunning = false;
  /** @type {IntersectionObserver | null} */
  static #swiperVisibilityObserver = null;

  static get observedAttributes() {
    return ['product-id'];
  }

  /**
   * Shared observer — inits Swiper only when a card nears the viewport.
   * INP risk: low — enqueues one card per intersection; no layout reads.
   * @returns {IntersectionObserver}
   */
  static #getSwiperVisibilityObserver() {
    if (this.#swiperVisibilityObserver) return this.#swiperVisibilityObserver;

    this.#swiperVisibilityObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const card = entry.target;
          if (!(card instanceof SearchDrawerProductCard)) continue;
          this.#swiperVisibilityObserver?.unobserve(card);
          card.#swiperObserved = false;
          card.#scheduleSwiperInit();
        }
      },
      { root: null, rootMargin: '200px 0px', threshold: 0.01 }
    );

    return this.#swiperVisibilityObserver;
  }

  /**
   * Runs at most one Swiper constructor per turn, yielding between cards.
   * INP risk: low — yields after each init so paint/input can run.
   * @returns {Promise<void>}
   */
  static async #drainSwiperInitQueue() {
    if (this.#swiperDrainRunning) return;
    this.#swiperDrainRunning = true;

    try {
      while (this.#pendingSwiperInits.size) {
        const card = this.#pendingSwiperInits.values().next().value;
        this.#pendingSwiperInits.delete(card);
        if (card.isConnected) await card.#initSwiper();
        await yieldToMainThread();
      }
    } finally {
      this.#swiperDrainRunning = false;
      if (this.#pendingSwiperInits.size) void this.#drainSwiperInitQueue();
    }
  }

  /**
   * Sets the product data and updates attributes for external consumers.
   * @param {object|null} value
   */
  set product(value) {
    this.#updateProduct(value);
  }

  /**
   * Updates product data without reordering the color swatch buttons.
   * @param {object|null} value
   */
  syncProduct(value) {
    this.#updateProduct(value, { preserveColorVariantOrder: true });
  }

  /**
   * @param {object|null} value
   * @param {{ preserveColorVariantOrder?: boolean }} [options]
   */
  #updateProduct(value, { preserveColorVariantOrder = false } = {}) {
    this.#product = value;
    this.#swatchHydratePromise = null;

    if (!value?.handle) return;

    setCachedProduct(String(value.id), value);
    this.setAttribute('product-id', String(value.id || ''));
    this.setAttribute('product-handle', String(value.handle));

    // Liquid already painted this card — bind data only, never full re-render on first paint.
    if (this.#isLiquidSsrPainted()) {
      this.setAttribute('data-ssr-final', '');
      if (!preserveColorVariantOrder || !this.hasAttribute('color-variant-ids')) {
        this.setAttribute(
          'color-variant-ids',
          JSON.stringify(getColorVariantProductIds(value))
        );
      }
      if (this.isConnected) {
        if (preserveColorVariantOrder) {
          this.#promoteSsrCardForInteraction();
          this.#render();
          void this.hydrateColorSwatches();
        } else {
          this.#hydrateSsrCarousel();
          this.hydrateColorSwatches();
        }
      }
      return;
    }

    if (!preserveColorVariantOrder || !this.hasAttribute('color-variant-ids')) {
      this.setAttribute(
        'color-variant-ids',
        JSON.stringify(getColorVariantProductIds(value))
      );
    }

    if (this.isConnected) {
      this.#render();
      this.hydrateColorSwatches();
    }
  }

  /**
   * Clears SSR markers so the card can fully re-render after a color swatch click.
   */
  #promoteSsrCardForInteraction() {
    this.removeAttribute('data-ssr-painted');
    this.removeAttribute('data-ssr-final');
    this.removeAttribute('data-ssr-handle');
    this.querySelector('[data-ssr-static-inner]')?.removeAttribute('data-ssr-static-inner');
  }

  /**
   * True when Liquid SSR already painted this card into the HTML response.
   * Attribute markers are enough — do not depend on image DOM shape.
   * @returns {boolean}
   */
  #isLiquidSsrPainted() {
    return (
      this.hasAttribute('data-ssr-final') ||
      this.hasAttribute('data-ssr-painted') ||
      this.hasAttribute('data-ssr-handle') ||
      Boolean(this.querySelector('[data-ssr-static-inner]'))
    );
  }

  /**
   * @returns {object|null}
   */
  get product() {
    return this.#product;
  }

  /**
   * Returns cached product data for the active product on this card.
   * @returns {object|undefined}
   */
  getCachedProductData() {
    const productId = this.getAttribute('product-id');
    return productId ? getCachedProduct(productId) : undefined;
  }

  connectedCallback() {
    if (this.#product) {
      // SSR cards stay as Liquid HTML forever, but still need overlay + carousel hydration.
      if (!this.#isLiquidSsrPainted()) {
        this.#render();
      } else {
        this.#hydrateSsrCarousel();
      }
      this.hydrateColorSwatches();
    }

    this.addEventListener('click', this.#onClick);
    this.addEventListener('mouseleave', this.#onMouseLeave);
    this.addEventListener('pointerenter', this.#onPointerEnter);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('mouseleave', this.#onMouseLeave);
    this.removeEventListener('pointerenter', this.#onPointerEnter);
    this.#teardownSwiperScheduling();
    this.#destroySwiper();
  }

  /**
   * Fetches color-swatch data via Storefront GraphQL from this component.
   * Does not replace the Boost/API product used for the initial card render.
   * @returns {Promise<void>}
   */
  async hydrateColorSwatches() {
    if (this.#swatchHydratePromise) return this.#swatchHydratePromise;

    this.#swatchHydratePromise = this.#runColorSwatchHydration();
    try {
      await this.#swatchHydratePromise;
    } finally {
      this.#swatchHydratePromise = null;
    }
  }

  /**
   * @returns {Promise<void>}
   */
  async #runColorSwatchHydration() {
    const productId = this.getAttribute('product-id');
    const colorVariantIds = this.#getColorVariantIds();
    const idsToFetch = buildProductIdsForFetch(productId, colorVariantIds);

    if (!idsToFetch.length) {
      this.applySwatchColors();
      return;
    }

    await fetchAndCacheColorVariantProducts(idsToFetch);

    const cachedProduct = productId ? getCachedProduct(productId) : null;
    if (cachedProduct?.handle) {
      const prevIds = this.#getColorVariantIds();
      const fromProduct = filterRenderableColorVariantIds(getColorVariantProductIds(cachedProduct));
      // Keep existing swatch order after color clicks; only seed order on first hydrate.
      const nextIds = prevIds.length
        ? resolveColorVariantIdsForRender(prevIds, this.#product || cachedProduct)
        : ensureCurrentProductFirstSwatch(fromProduct, this.#product || cachedProduct);

      if (JSON.stringify(nextIds) !== JSON.stringify(prevIds)) {
        this.setAttribute('color-variant-ids', JSON.stringify(nextIds));
        const expandedIds = buildProductIdsForFetch(productId, nextIds);
        await fetchAndCacheColorVariantProducts(expandedIds);
      }

      // Keep API product on the card; cache may hold merged swatch metafields for siblings.
      if (this.#product && !isGraphqlEnrichedProduct(this.#product)) {
        const mergedSelf = getCachedProduct(String(this.#product.id));
        if (mergedSelf?.handle && mergedSelf.swatchEnrichedBy === 'storefront') {
          this.#product = mergedSelf;
        }
      }
    }

    this.applySwatchColors();
  }

  /**
   * Applies cached swatch colors without fetching.
   */
  applySwatchColors() {
    this.#renderColorSwatches();
  }

  /**
   * @returns {string[]}
   */
  #getColorVariantIds() {
    try {
      const parsed = JSON.parse(this.getAttribute('color-variant-ids') || '[]');
      return Array.isArray(parsed) ? parsed.map(String).filter(Boolean) : [];
    } catch {
      return [];
    }
  }

  #destroySwiper() {
    const swiperRoot = this.querySelector('[data-product-card-swiper]');
    const swiperElement = /** @type {HTMLElement & { __swiper?: import('swiper').default }} */ (swiperRoot);

    if (!swiperElement?.__swiper) return;

    swiperElement.__swiper.destroy(true, true);
    swiperElement.__swiper = null;
    this.#swiper = null;
  }

  #teardownSwiperScheduling() {
    SearchDrawerProductCard.#pendingSwiperInits.delete(this);
    this.#swiperScheduled = false;

    if (this.#swiperObserved && SearchDrawerProductCard.#swiperVisibilityObserver) {
      SearchDrawerProductCard.#swiperVisibilityObserver.unobserve(this);
      this.#swiperObserved = false;
    }
  }

  /**
   * Queues this card for serialized Swiper init (avoids one long microtask of N mounts).
   */
  #scheduleSwiperInit() {
    if (this.#swiper || this.#swiperScheduled) return;
    this.#swiperScheduled = true;
    SearchDrawerProductCard.#pendingSwiperInits.add(this);
    void SearchDrawerProductCard.#drainSwiperInitQueue();
  }

  /**
   * Defers Swiper until near viewport (or pointerenter / high-priority enqueue).
   */
  #observeForSwiper() {
    if (this.#swiper || this.#swiperScheduled || this.#swiperObserved) return;
    this.#swiperObserved = true;
    SearchDrawerProductCard.#getSwiperVisibilityObserver().observe(this);
  }

  /**
   * Waits until Swiper is available on window.
   * @returns {Promise<typeof import('swiper').default | undefined>}
   */
  async #waitForSwiper() {
    if (window.Swiper) return window.Swiper;

    return new Promise((resolve) => {
      const check = () => {
        if (window.Swiper) {
          resolve(window.Swiper);
          return;
        }
        requestAnimationFrame(check);
      };
      check();
    });
  }

  /**
   * Initializes Swiper on the card image carousel.
   * @returns {Promise<void>}
   */
  async #initSwiper() {
    const SwiperConstructor = await this.#waitForSwiper();
    if (!SwiperConstructor || !this.isConnected) return;

    const swiperRoot = this.querySelector('[data-product-card-swiper]');
    const swiperElement = /** @type {HTMLElement & { __swiper?: import('swiper').default }} */ (swiperRoot);

    if (!swiperElement || swiperElement.__swiper) return;

    const media = swiperElement.closest('.search-drawer-product-card__media');
    const prevEl = media?.querySelector('[data-swiper-prev]');
    const nextEl = media?.querySelector('[data-swiper-next]');
    const paginationEl = media?.querySelector('[data-product-card-pagination]');
    const slideCount = swiperElement.querySelectorAll('.swiper-slide').length;

    if (slideCount < 2) return;

    this.#swiper = new SwiperConstructor(swiperElement, {
      slidesPerView: 1,
      nested: true,
      spaceBetween: 0,
      loop: true,
      watchOverflow: true,
      navigation: {
        prevEl: prevEl || undefined,
        nextEl: nextEl || undefined,
      },
      pagination: paginationEl
        ? {
            el: paginationEl,
            type: 'progressbar',
          }
        : undefined,
    });

    swiperElement.__swiper = this.#swiper;
  }

  /**
   * Renders the full card markup; Swiper mounts lazily (visible / hover / priority).
   * Liquid SSR cards are never rebuilt — they stay as painted in HTML.
   */
  #render() {
    if (!this.#product?.handle) return;
    if (this.#isLiquidSsrPainted()) return;

    this.#destroySwiper();
    this.#teardownSwiperScheduling();
    this.innerHTML = this.#buildCardHtml(this.#product);

    const imagePriority = this.getAttribute('data-image-priority');
    if (imagePriority === 'high' || imagePriority === 'eager') {
      this.#scheduleSwiperInit();
    } else {
      this.#observeForSwiper();
    }
  }

  #onPointerEnter = () => {
    if (this.#isLiquidSsrPainted()) {
      this.#hydrateSsrCarousel();
    }
    this.#scheduleSwiperInit();
  };

  /**
   * Adds missing image slides, arrows, and pagination to Liquid SSR cards
   * so hover nav matches JS-rendered cards without rebuilding the whole card.
   */
  #hydrateSsrCarousel() {
    if (!this.#product?.handle || !this.#isLiquidSsrPainted()) return;

    const images = getProductImages(this.#product);
    if (images.length < 2) return;

    const media = this.querySelector('.search-drawer-product-card__media');
    const swiperRoot = media?.querySelector('.search-drawer-product-card__swiper');
    const wrapper = swiperRoot?.querySelector('.swiper-wrapper');
    if (!(media instanceof HTMLElement) || !(swiperRoot instanceof HTMLElement) || !(wrapper instanceof HTMLElement)) {
      return;
    }

    swiperRoot.setAttribute('data-product-card-swiper', '');

    const config = getSearchDrawerProductConfig();
    const icons = config.icons || {};
    const handle = String(this.#product.handle);
    const title = String(this.#product.title || '');
    const productUrl = `/products/${encodeURIComponent(handle)}`;
    const existingSlideCount = wrapper.querySelectorAll('.swiper-slide').length;

    for (let index = existingSlideCount; index < images.length; index += 1) {
      const imageSrc = images[index];
      if (!imageSrc) continue;

      const slide = document.createElement('div');
      slide.className = 'swiper-slide';
      slide.innerHTML = `<a href="${escapeHtml(productUrl)}" class="search-drawer-product-card__media-link">
        <img
          class="search-drawer-product-card__image"
          src="${escapeHtml(imageSrc)}"
          alt="${escapeHtml(title)}"
          loading="lazy"
          decoding="async"
          width="683"
          height="1024"
        >
      </a>`;
      wrapper.appendChild(slide);
    }

    if (!media.querySelector('[data-product-card-pagination]')) {
      const pagination = document.createElement('div');
      pagination.className = 'search-drawer-product-card__pagination swiper-pagination';
      pagination.setAttribute('data-product-card-pagination', '');
      pagination.setAttribute('aria-hidden', 'true');
      media.appendChild(pagination);
    }

    if (!media.querySelector('.search-drawer-product-card__arrows')) {
      const arrows = document.createElement('div');
      arrows.className = 'search-drawer-product-card__arrows';
      arrows.innerHTML = `<button
          type="button"
          class="search-drawer-product-card__arrow search-drawer-product-card__arrow--prev"
          data-swiper-prev
          aria-label="Previous image"
        >${icons.chevronLeft || ''}</button>
        <button
          type="button"
          class="search-drawer-product-card__arrow search-drawer-product-card__arrow--next"
          data-swiper-next
          aria-label="Next image"
        >${icons.chevronRight || ''}</button>`;
      media.appendChild(arrows);
    }

    const imagePriority = this.getAttribute('data-image-priority');
    if (imagePriority === 'high' || imagePriority === 'eager') {
      this.#scheduleSwiperInit();
    } else {
      this.#observeForSwiper();
    }
  }

  /**
   * Re-renders only the color swatch section after cache hydration.
   * For SSR cards, injects the full overlay if it doesn't exist yet.
   */
  #renderColorSwatches() {
    if (!this.#product) return;

    const colorVariantIds = resolveColorVariantIdsForRender(this.#getColorVariantIds(), this.#product);
    const activeIndex = getActiveColorVariantIndex(colorVariantIds, this.#product);
    const productUrl = `/products/${encodeURIComponent(String(this.#product.handle))}`;
    const sizeVariants = getSizeVariants(this.#product.variants);

    let container = this.querySelector('[data-color-variants]');

    if (!(container instanceof HTMLElement)) {
      if (!colorVariantIds.length && !sizeVariants.length) return;

      const media = this.querySelector('.search-drawer-product-card__media');
      if (!(media instanceof HTMLElement)) return;

      const overlay = document.createElement('div');
      overlay.className = 'search-drawer-product-card__overlay';

      if (colorVariantIds.length) {
        const colorWrapper = document.createElement('div');
        colorWrapper.className = 'search-drawer-product-card__color-variants';
        colorWrapper.setAttribute('data-color-variants', '');
        colorWrapper.innerHTML = this.#buildColorVariantsInnerHtml(colorVariantIds, activeIndex, productUrl);
        overlay.appendChild(colorWrapper);
        container = colorWrapper;
      }

      if (sizeVariants.length) {
        const sizesWrapper = document.createElement('div');
        sizesWrapper.className = 'search-drawer-product-card__sizes';
        sizesWrapper.setAttribute('role', 'group');
        sizesWrapper.setAttribute('aria-label', 'Select size');
        sizesWrapper.innerHTML = sizeVariants
          .map((variant) => {
            const sizeLabel = String(variant?.title || '');
            const isAvailable = variant?.available !== false;
            return `<button
              type="button"
              class="search-drawer-product-card__size${isAvailable ? '' : ' search-drawer-product-card__size--unavailable'}"
              data-action="add-size"
              data-variant-id="${escapeHtml(String(variant?.id || ''))}"
              data-product-handle="${escapeHtml(String(this.#product.handle))}"
              ${isAvailable ? '' : 'disabled'}
              aria-label="Add size ${escapeHtml(sizeLabel)} to cart"
            >${buildAddToCartButtonStatesMarkup(sizeLabel)}</button>`;
          })
          .join('');
        overlay.appendChild(sizesWrapper);
      }

      media.appendChild(overlay);
      this.setAttribute('color-variant-ids', JSON.stringify(colorVariantIds));
      return;
    }

    if (!colorVariantIds.length) {
      container.replaceChildren();
    } else {
      this.setAttribute('color-variant-ids', JSON.stringify(colorVariantIds));
      container.innerHTML = this.#buildColorVariantsInnerHtml(colorVariantIds, activeIndex, productUrl);
    }

    const overlay = container.closest('.search-drawer-product-card__overlay');
    const sizesWrapper = overlay?.querySelector('.search-drawer-product-card__sizes');

    if (!sizeVariants.length) {
      sizesWrapper?.remove();
      return;
    }

    const sizesHtml = sizeVariants
      .map((variant) => {
        const sizeLabel = String(variant?.title || '');
        const isAvailable = variant?.available !== false;
        return `<button
          type="button"
          class="search-drawer-product-card__size${isAvailable ? '' : ' search-drawer-product-card__size--unavailable'}"
          data-action="add-size"
          data-variant-id="${escapeHtml(String(variant?.id || ''))}"
          data-product-handle="${escapeHtml(String(this.#product.handle))}"
          ${isAvailable ? '' : 'disabled'}
          aria-label="Add size ${escapeHtml(sizeLabel)} to cart"
        >${buildAddToCartButtonStatesMarkup(sizeLabel)}</button>`;
      })
      .join('');

    if (sizesWrapper instanceof HTMLElement) {
      sizesWrapper.innerHTML = sizesHtml;
      return;
    }

    if (!(overlay instanceof HTMLElement)) return;

    const sizesEl = document.createElement('div');
    sizesEl.className = 'search-drawer-product-card__sizes';
    sizesEl.setAttribute('role', 'group');
    sizesEl.setAttribute('aria-label', 'Select size');
    sizesEl.innerHTML = sizesHtml;
    overlay.appendChild(sizesEl);
  }

  /**
   * @param {object} product
   * @returns {string}
   */
  #buildCardHtml(product) {
    const config = getSearchDrawerProductConfig();
    const icons = config.icons || {};
    const handle = String(product.handle);
    const title = String(product.title || '');
    const productUrl = `/products/${encodeURIComponent(handle)}`;
    const images = getProductImages(product);
    const badge = getProductCardTag(product);
    const priceMin = resolveBoostPrice(product, 'price_min');
    const priceMax = resolveBoostPrice(product, 'price_max');
    const price = Number.isFinite(priceMin) ? priceMin : priceMax;
    const compareAtMin = resolveBoostPrice(product, 'compare_at_price_min');
    const compareAtMax = resolveBoostPrice(product, 'compare_at_price_max');
    const compareAt = Number.isFinite(compareAtMin)
      ? compareAtMin
      : Number.isFinite(compareAtMax)
        ? compareAtMax
        : null;
    const isOnSale = Number(compareAt) > Number(price);
    const activeCurrency = getActiveCurrencyCode() || config.currency;
    const sizeVariants = getSizeVariants(product.variants);
    const colorVariantIds = resolveColorVariantIdsForRender(this.#getColorVariantIds(), product);
    const activeColorVariantIndex = getActiveColorVariantIndex(colorVariantIds, product);
    const hasMultipleImages = images.length > 1;
    const imagePriority = this.getAttribute('data-image-priority');
    const prioritizeImage = imagePriority === 'high' || imagePriority === 'eager';

    const slidesHtml = (images.length ? images : [''])
      .map(
        (imageSrc, imageIndex) => {
          const isPrioritySlide = prioritizeImage && imageIndex === 0;
          return `<div class="swiper-slide">
          <a href="${escapeHtml(productUrl)}" class="search-drawer-product-card__media-link">
            <img
              class="search-drawer-product-card__image"
              src="${escapeHtml(imageSrc)}"
              alt="${escapeHtml(title)}"
              loading="${isPrioritySlide ? 'eager' : 'lazy'}"
              ${isPrioritySlide ? 'fetchpriority="high"' : ''}
              decoding="async"
              width="683"
              height="1024"
            >
          </a>
        </div>`;
        }
      )
      .join('');

    const sizesHtml = sizeVariants
      .map((variant) => {
        const sizeLabel = String(variant?.title || '');
        const isAvailable = variant?.available !== false;
        const variantId = variant?.id;

        return `<button
          type="button"
          class="search-drawer-product-card__size${isAvailable ? '' : ' search-drawer-product-card__size--unavailable'}"
          data-action="add-size"
          data-variant-id="${escapeHtml(String(variantId || ''))}"
          data-product-handle="${escapeHtml(handle)}"
          ${isAvailable ? '' : 'disabled'}
          aria-label="Add size ${escapeHtml(sizeLabel)} to cart"
        >${buildAddToCartButtonStatesMarkup(sizeLabel)}</button>`;
      })
      .join('');

    const colorVariantsHtml = this.#buildColorVariantsHtml(
      colorVariantIds,
      activeColorVariantIndex,
      productUrl
    );

    const overlayHtml =
      colorVariantsHtml || sizesHtml
        ? `<div class="search-drawer-product-card__overlay">
            ${colorVariantsHtml}
            ${
              sizesHtml
                ? `<div class="search-drawer-product-card__sizes" role="group" aria-label="Select size">
                    ${sizesHtml}
                  </div>`
                : ''
            }
          </div>`
        : '';

    const badgeHtml = badge
      ? `<span class="search-drawer-product-card__badge">${escapeHtml(String(badge).toLowerCase())}</span>`
      : '';

    const compareHtml =
      isOnSale && compareAt != null
        ? `<span class="search-drawer-product-card__price-compare">${escapeHtml(formatMajorMoney(compareAt, config.moneyFormat, activeCurrency))}</span>`
        : '';

    const arrowsHtml = hasMultipleImages
      ? `<div class="search-drawer-product-card__arrows">
          <button
            type="button"
            class="search-drawer-product-card__arrow search-drawer-product-card__arrow--prev"
            data-swiper-prev
            aria-label="Previous image"
          >${icons.chevronLeft || ''}</button>
          <button
            type="button"
            class="search-drawer-product-card__arrow search-drawer-product-card__arrow--next"
            data-swiper-next
            aria-label="Next image"
          >${icons.chevronRight || ''}</button>
        </div>`
      : '';

    const paginationHtml = hasMultipleImages
      ? `<div
          class="search-drawer-product-card__pagination swiper-pagination"
          data-product-card-pagination
          aria-hidden="true"
        ></div>`
      : '';

    return `<div class="search-drawer-product-card__inner">
      <div class="search-drawer-product-card__media">
        <div class="swiper search-drawer-product-card__swiper" data-product-card-swiper>
          <div class="swiper-wrapper">
            ${slidesHtml}
          </div>
        </div>
        ${badgeHtml}
        ${paginationHtml}
        ${arrowsHtml}
        ${overlayHtml}
      </div>
      <div class="search-drawer-product-card__info">
        <div class="search-drawer-product-card__details">
          <a href="${escapeHtml(productUrl)}" class="search-drawer-product-card__title">
            ${escapeHtml(title.toLowerCase())}
          </a>
          <div class="search-drawer-product-card__price">
            <span class="search-drawer-product-card__price-current">${escapeHtml(formatMajorMoney(price, config.moneyFormat, activeCurrency))}</span>
            ${compareHtml}
          </div>
        </div>
        <button
          type="button"
          class="search-drawer-product-card__cart button-unstyled"
          data-action="cart-icon"
          data-product-id="${escapeHtml(String(product.id || ''))}"
          data-product-handle="${escapeHtml(handle)}"
          aria-label="Open cart for ${escapeHtml(title)}"
        >
          <span class="svg-wrapper" aria-hidden="true">${icons.cart || ''}</span>
        </button>
      </div>
    </div>`;
  }

  /**
   * @param {string[]} colorVariantIds
   * @param {number} activeIndex
   * @param {string} productUrl
   * @returns {string}
   */
  #buildColorVariantsHtml(colorVariantIds, activeIndex, productUrl) {
    const renderableIds = filterRenderableColorVariantIds(colorVariantIds);
    if (!renderableIds.length) return '';

    return `<div class="search-drawer-product-card__color-variants" data-color-variants>
      ${this.#buildColorVariantsInnerHtml(renderableIds, activeIndex, productUrl)}
    </div>`;
  }

  /**
   * @param {string[]} colorVariantIds
   * @param {number} activeIndex
   * @param {string} productUrl
   * @returns {string}
   */
  #buildColorVariantsInnerHtml(colorVariantIds, activeIndex, productUrl) {
    const renderableIds = filterRenderableColorVariantIds(colorVariantIds);
    if (!renderableIds.length) return '';

    const resolvedActiveIndex = getActiveColorVariantIndex(
      renderableIds,
      this.#product || { id: renderableIds[activeIndex] }
    );
    const swatchesHtml = this.#buildColorSwatchesHtml(renderableIds, resolvedActiveIndex);
    const hasMore = renderableIds.length > MAX_COLOR_SWATCHES;
    const moreHtml = hasMore
      ? `<a href="${escapeHtml(productUrl)}" class="search-drawer-product-card__color-more">+ MORE</a>`
      : '';

    return `<div
      class="search-drawer-product-card__color-swatches"
      role="list"
      aria-label="Color variants"
    >
      ${swatchesHtml}
    </div>
    ${moreHtml}`;
  }

  /**
   * @param {string[]} colorVariantIds
   * @param {number} activeIndex
   * @returns {string}
   */
  #buildColorSwatchesHtml(colorVariantIds, activeIndex) {
    const renderableIds = filterRenderableColorVariantIds(colorVariantIds);
    if (!renderableIds.length) return '';

    const swatchColorCache = getSwatchColorCache();
    const visibleVariantIds = renderableIds.slice(0, MAX_COLOR_SWATCHES);
    const activeVariantId = renderableIds[activeIndex] || '';

    const swatchesHtml = visibleVariantIds
      .map((variantId) => {
        const isActive = variantId === activeVariantId;
        const swatchColor = swatchColorCache.get(String(variantId));
        const loadedClass = swatchColor
          ? ' search-drawer-product-card__color-swatch--loaded'
          : ' search-drawer-product-card__color-swatch--skeleton';
        const whiteClass = swatchColor && isWhiteSwatchColor(swatchColor)
          ? ' search-drawer-product-card__color-swatch--white'
          : '';
        const swatchStyle = swatchColor
          ? ` style="--swatch-color: ${escapeHtml(swatchColor)}"`
          : '';

        return `<button
          type="button"
          class="search-drawer-product-card__color-swatch${loadedClass}${whiteClass}${isActive ? ' search-drawer-product-card__color-swatch--active' : ''}"
          data-action="select-color"
          data-color-variant-id="${escapeHtml(variantId)}"
          aria-label="Color variant"
          ${isActive ? 'aria-current="true"' : ''}${swatchStyle}
        ></button>`;
      })
      .join('');

    const productId = this.getAttribute('product-id');
    if (productId) {
      logProductSwatchColors(productId, renderableIds);
    }

    return swatchesHtml;
  }

  /**
   * Restores overlay visibility on the next hover after quick view is triggered.
   */
  #onMouseLeave = () => {
    this.removeAttribute('data-overlay-hidden');
  };

  /**
   * @param {Event} event
   */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const actionButton = target.closest('[data-action]');
    if (!(actionButton instanceof HTMLElement) || !this.contains(actionButton)) return;

    const action = actionButton.getAttribute('data-action');

    if (action === 'cart-icon') {
      event.preventDefault();
      event.stopPropagation();

      this.setAttribute('data-overlay-hidden', '');
      actionButton.blur();

      this.dispatchEvent(
        new CustomEvent('search-drawer-product-card:cart', {
          bubbles: true,
          detail: {
            productId: actionButton.getAttribute('data-product-id'),
            handle: actionButton.getAttribute('data-product-handle'),
            product: this.#product,
            cachedProduct: this.getCachedProductData(),
            trigger: actionButton,
          },
        })
      );
      return;
    }

    if (action === 'add-size') {
      event.preventDefault();
      event.stopPropagation();

      if (actionButton.hasAttribute('disabled')) return;
      if (actionButton.hasAttribute('data-loading') || actionButton.hasAttribute('data-success')) return;

      const variantId = actionButton.getAttribute('data-variant-id');
      this.#addVariantToCart(variantId, actionButton);
      return;
    }

    if (action === 'select-color') {
      event.preventDefault();
      event.stopPropagation();

      const variantProductId = actionButton.getAttribute('data-color-variant-id');
      if (variantProductId) this.#handleColorSwatchSelect(variantProductId);
    }
  };

  /**
   * @param {string|null} variantProductId
   */
  async #handleColorSwatchSelect(variantProductId) {
    const activeProductId = this.getAttribute('product-id');

    if (!variantProductId || activeProductId === variantProductId) return;

    this.toggleAttribute('data-card-loading', true);

    try {
      await fetchAndCacheColorVariantProducts([variantProductId]);

      const product = getCachedProduct(variantProductId);
      if (!product) return;

      this.syncProduct(product);
    } finally {
      this.toggleAttribute('data-card-loading', false);
    }
  }

  /**
   * @param {string|null} variantId
   * @param {HTMLElement} button
   */
  async #addVariantToCart(variantId, button) {
    if (!variantId) return;
    if (button.hasAttribute('data-loading') || button.hasAttribute('data-success')) return;

    const cartAddUrl = window.Theme?.routes?.cart_add_url || '/cart/add.js';

    button.toggleAttribute('data-loading', true);
    button.toggleAttribute('data-success', false);
    button.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          id: Number(variantId),
          quantity: 1,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result?.description || result?.message || 'Add to cart failed');
      }

      button.toggleAttribute('data-loading', false);
      button.toggleAttribute('data-success', true);
      button.removeAttribute('aria-busy');
      setTimeout(() => button.toggleAttribute('data-success', false), ADD_TO_CART_SUCCESS_DURATION_MS);

      dispatchCartUpdate('search-drawer-product-card', this.getAttribute('product-id'));
    } catch (error) {
      button.toggleAttribute('data-loading', false);
      button.removeAttribute('aria-busy');
      console.error('[search-drawer-product-card] add to cart failed', error);
    }
  }
}

if (!customElements.get('search-drawer-product-card')) {
  customElements.define('search-drawer-product-card', SearchDrawerProductCard);
}

/**
 * Fetches the latest cart and dispatches the theme `cart:update` event using the
 * shape the theme's listeners expect (cart-icon reads `detail.data.itemCount`,
 * header-actions reads `detail.resource.item_count`). The `/cart/add.js` response
 * doesn't include the total item count, so the current cart is fetched first.
 *
 * @param {string} source - Identifier of the component that added to cart.
 * @param {string|null} [productId] - Product id that was added.
 * @returns {Promise<void>}
 */
export async function dispatchCartUpdate(source, productId = null) {
  let cart = null;

  try {
    const response = await fetch('/cart.js');
    if (response.ok) cart = await response.json();
  } catch (error) {
    console.error('[search-drawer-product-card] cart fetch failed', error);
  }

  document.dispatchEvent(
    new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: cart,
        sourceId: source,
        data: {
          source,
          itemCount: cart?.item_count ?? 0,
          productId,
        },
      },
    })
  );
}

/**
 * Builds the list of product IDs to fetch for a card (card product + color variants).
 * @param {string|null} productId
 * @param {string[]} colorVariantIds
 * @returns {string[]}
 */
function buildProductIdsForFetch(productId, colorVariantIds) {
  const ids = new Set();

  if (productId) ids.add(String(productId));

  (Array.isArray(colorVariantIds) ? colorVariantIds : []).forEach((id) => {
    if (id) ids.add(String(id));
  });

  return [...ids];
}

/**
 * Collects product IDs for GraphQL fetch from all cards in a list.
 * Always includes each card's product-id plus its color variant IDs.
 * @param {ParentNode|null} list
 * @param {NodeListOf<Element>|Element[]|null} [cardElements]
 * @returns {string[]}
 */
export function collectColorVariantIdsFromCards(list, cardElements = null) {
  const cards = cardElements
    ? Array.from(cardElements)
    : Array.from(list?.querySelectorAll?.('search-drawer-product-card') || []);
  const ids = new Set();

  cards.forEach((card) => {
    const productId = card.getAttribute('product-id');
    let colorVariantIds = [];

    try {
      const parsed = JSON.parse(card.getAttribute('color-variant-ids') || '[]');
      if (Array.isArray(parsed)) colorVariantIds = parsed;
    } catch {
      colorVariantIds = [];
    }

    buildProductIdsForFetch(productId, colorVariantIds).forEach((id) => ids.add(id));
  });

  return [...ids];
}

/**
 * Asks each card to fetch its own GraphQL color swatches.
 * Initial card markup always comes from the API/product payload already on the card.
 * @param {HTMLElement} list
 * @param {{ cards?: NodeListOf<Element>|Element[] }} [options]
 * @returns {Promise<void>}
 */
export async function hydrateSearchDrawerProductCards(list, options = {}) {
  const cards = options.cards
    ? Array.from(options.cards)
    : Array.from(list?.querySelectorAll?.('search-drawer-product-card') || []);

  await Promise.all(
    cards.map((card) => {
      if (!(card instanceof SearchDrawerProductCard)) return Promise.resolve();
      return card.hydrateColorSwatches();
    })
  );
}

export { SearchDrawerProductCard };

/**
 * Builds a search drawer product list item with a hydrated product card.
 * @param {object} product
 * @returns {HTMLLIElement}
 */
export function buildSearchDrawerProductListItem(product) {
  const li = document.createElement('li');
  li.className = 'search-drawer-modal__product';

  const card = document.createElement('search-drawer-product-card');
  card.className = 'search-drawer-product-card';
  card.product = product;

  li.appendChild(card);
  return li;
}
